import {
   API_ENDPOINTS,
   BottomAnchoredControls,
   Button,
   ButtonTypes,
   Card,
   CardBody,
   customToast,
   Icon,
   LinkifyText,
   Modal,
   PageLayoutPublic,
   ProgressBar,
   route,
   Stepper,
   StringHelpers,
   useDisabledContext,
   useForm,
   useLangContext,
   useNavigationGuard,
   useScrollToError,
   useStepper,
   Variants,
   WidthConstrainedContainer,
} from 'carrier-fe';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StageOne from '../../components/lookup-journey/Step1';
import StageTwo from '../../components/lookup-journey/Step2';
import axios from 'axios';
import { STEPS } from '../../Constants/InstallationJourney';

const loadPageLang = async (
   pageLang: any,
   getLangPage: Function,
   setPageLang: Dispatch<SetStateAction<any>>
) => {
   if (pageLang) return;
   try {
      const response = await getLangPage('registration/householder/lookup');
      setPageLang(response);
   } catch (error: any) {
      console.error(error);
   }
};

const TOTAL_STEPS = 1;
const DEFAULT_NAVIGATION_GUARD = 'You have unsaved changes. Are you sure you want to leave?';

const defaultData = {
   serial_number: '',
   installation_postcode: '',
};

export type HouseholdLookupFormData = typeof defaultData;

function HouseholdLookup() {
   const { crud,getLangPage } = useLangContext();
   const { disabled, setDisabled } = useDisabledContext();
   const [pageLang, setPageLang] = useState<any>(null);
   const [loading, setLoading] = useState(false);
   const [helpModalOpen, setHelpModalOpen] = useState(false);

   const { activeStep, nextStep, prevStep, isFirstStep, isLastStep } = useStepper({
      totalSteps: TOTAL_STEPS,
   });

   useNavigationGuard({
      message: crud?.modals?.alert?.description?.at(0) || DEFAULT_NAVIGATION_GUARD,
      isEnabled: true,
   });

   const navigate = useNavigate();

   useEffect(() => {
      loadPageLang(pageLang, getLangPage, setPageLang);
   }, []);

   const submit = async (data: Partial<HouseholdLookupFormData>): Promise<any> => {
      const payload = {
         [STEPS.STEP1]: {
            serial_number: data?.serial_number,
            installation_postcode: data?.installation_postcode,
         }
      }[activeStep];
      const response = await axios.post(
         route(`${API_ENDPOINTS.HOUSEHOLDER.LOOKUP.VIEW}?step=${activeStep}`),
         payload
      );
      if (response.status === 200) {
         if (response.data?.data && isLastStep) {
            const id = response.data?.data?.id;
            customToast({
               title: response?.data?.message,
               variant: Variants.Success,
            });
            setDisabled(false);
            navigate(`/installation/${id}`);
         } else {
            customToast({
               title: response?.data?.message,
               variant: Variants.Success,
            });
            nextStep();
         }
      }
   };

   const form = useForm(submit, defaultData,{
      silentSuccess: true,
   });
   const formRef = useScrollToError(form.errors);
   const { Submit } = form;

   const handleBack = () => {
      if (isFirstStep) {
         navigate(-1);
      } else {
         prevStep();
      }
   };

   const btnLabel = useMemo(() => {
      const stepType = isLastStep ? 'submit' : 'next';
      const defaultLabel = crud?.buttons?.[stepType]?.default || (isLastStep ? 'Submit' : 'Next');
      const loadingLabel =
         crud?.buttons?.[stepType]?.submitting || (isLastStep ? 'Submitting...' : 'Saving...');

      return { loadingLabel, defaultLabel };
   }, [isLastStep, crud]);

   return (
      <PageLayoutPublic
         progressBar={{ activeStep, totalSteps: TOTAL_STEPS }}
         title="Lookup Installation"
      >
         <WidthConstrainedContainer>
            <Card>
               <CardBody>
                  <div ref={formRef}>
                     <Stepper
                        title={StringHelpers.upper(`stage ${activeStep}/${TOTAL_STEPS}`)}
                        activeStep={activeStep}
                        rightSlot={
                           <>
                              <div
                                 onClick={() => setHelpModalOpen(true)}
                                 role="button"
                                 className="pe d-flex align-items-center gap-1 text-primary"
                              >
                                 <span className="fw-semibold" style={{ fontSize: '17px' }}>
                                    {pageLang?.page?.help_modal?.title || 'Help'}
                                 </span>
                                 <Icon icon={'help'} />
                              </div>
                              <Modal
                                 open={helpModalOpen}
                                 onClose={() => setHelpModalOpen(false)}
                                 title={pageLang?.page?.help_modal?.title || 'Help'}
                              >
                                 <LinkifyText text={pageLang?.page?.help_modal?.description || "If you need help locating your Viessmann product's serial number, visit https://www.viessmann.co.uk/en/support/spare-parts-app/serial-number-locator-for-viessmann-products.html for model-specific instructions and images."} />
                              </Modal>
                           </>
                        }
                     >
                        <StageTwo pageLang={pageLang} form={form} loading={loading} />
                     </Stepper>
                  </div>
               </CardBody>
            </Card>
         </WidthConstrainedContainer>

         <BottomAnchoredControls
            style={{
               position: 'fixed',
               bottom: 0,
               right: 0,
               padding: '1.5rem 2.5rem',
            }}
         >
            <div className="me-auto ms-5">
               <Button
                  label={StringHelpers.title(crud?.buttons?.exit?.default || 'Exit')}
                  onClick={() => {
                     window.confirm(crud?.modals?.alert?.description?.at(0)) && navigate('/');
                  }}
                  variant={Variants.Dark}
                  type={ButtonTypes.Outline}
                  disabled={disabled}
               />
            </div>
            {!isFirstStep && (
               <Button
                  label={crud?.buttons?.previous?.default || 'Previous'}
                  onClick={handleBack}
                  variant={Variants.Primary}
                  type={ButtonTypes.Outline}
                  className={'me-4'}
                  disabled={disabled}
               />
            )}
            <Submit
               disabled={disabled}
               defaultLabel={btnLabel.defaultLabel}
               loadingLabel={btnLabel.loadingLabel}
               useOnMouseDown
            />
         </BottomAnchoredControls>
      </PageLayoutPublic>
   );
}

export default HouseholdLookup;
